<template>
  <div
    class="sf-modal-white sf-modal sf-modal-top"
    :class="[
      className,
      sizeClassName,
      outerClassNames,
      { 'sf-modal-sticky-chrome': stickyChrome },
    ]"
  >
    <div
      ref="backgroundRef"
      class="sf-modal-backdrop"
      :class="backdropClassNames"
      data-testid="sf-modal-backdrop"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
    >
      <div
        class="sf-modal-container sf-below3"
        data-testid="sf-modal-container"
        :class="containerClassNames"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
      >
        <div
          ref="contentRef"
          class="sf-modal-inner"
          data-testid="sf-modal-inner"
          :class="[innerModalClasses, computedInnerClasses]"
          @mousedown.stop
          @mouseup.stop
        >
          <div ref="headerRef" class="sf-modal-header tw-relative tw-z-0">
            <div
              class="sf-modal-back sf-invisible"
              :class="{ 'tw-hidden': isFullHeight }"
            />
            <div
              class="sf-modal-title"
              :class="{ '!tw-px-6 !tw-py-3': isFullHeight }"
            >
              <slot name="title" />
            </div>
            <div
              :class="closeButtonClasses"
              data-testid="sf-modal-close"
              @click="handleCloseButtonClick"
            />
          </div>
          <div
            ref="messagesTopRef"
            class="sf-modal-messages tw-relative tw-z-0"
          >
            <slot name="messages" />
          </div>
          <div
            class="tw-relative"
            :class="[contentClassNames, stickyChrome ? 'tw-z-auto' : 'tw-z-10']"
          >
            <slot name="body" />
          </div>

          <div
            ref="messagesBottomRef"
            class="
              sf-modal-messages sf-modal-messages-bottom
              tw-relative tw-z-0
            "
          >
            <slot name="messages-bottom" />
          </div>

          <div
            ref="footerRef"
            class="tw-relative tw-z-0"
            :class="footerClassNames"
          >
            <slot name="footer" :close-after-animation="closeAfterAnimation" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type {
  FooterAlign,
  ModalAlign,
  ModalSize,
  ModalVariant,
} from './constants'
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useMotion } from '@vueuse/motion'

import {
  footerAlign as footerAlignConst,
  modalAlign,
  modalSizes,
  modalVariants,
} from './constants'
import useModalClassName from './use-modal-classname'
import useSetCssHeightVariable from '../../composables/use-set-css-height-variable'
import type { MotionAnimationsType } from '@app/shared/constants'
import { MotionAnimations } from '@app/shared/constants'
import { useBackdropClick } from '@app/shared/vue/composables/use-backdrop-click'

defineOptions({
  name: 'SfModal',
})

interface Props {
  align?: ModalAlign
  animated?: boolean
  backgroundAnimation?: MotionAnimationsType
  contentAnimation?: MotionAnimationsType
  className?: string
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  contentClassName?: string
  contentPadding?: number | null
  hideClose?: boolean
  innerModalClasses?: string
  footerAlign?: FooterAlign
  footerPadding?: number | null
  scrollableContent?: boolean
  size?: ModalSize
  // Pin the header and footer while the backdrop scrolls, and let the body's
  // dropdown menus overflow the modal instead of being clipped. Off by default;
  // pair with scrollableContent=false. See the .sf-modal-sticky-chrome styles.
  stickyChrome?: boolean
  variant?: ModalVariant
}

const props = withDefaults(defineProps<Props>(), {
  align: modalAlign.center,
  backgroundAnimation: 'fade',
  className: '',
  closeOnBackdropClick: true,
  closeOnEscape: true,
  contentAnimation: 'slideLeft',
  contentClassName: '',
  contentPadding: null,
  hideClose: false,
  footerAlign: footerAlignConst.center,
  footerPadding: null,
  innerModalClasses: '',
  scrollableContent: true,
  stickyChrome: false,
  size: modalSizes.s,
  variant: modalVariants.default,
})

const emit = defineEmits(['closeAttempt', 'close'])

const backgroundRef = ref<HTMLElement | null>(null)

const backgroundMotion = useMotion(
  props.animated ? backgroundRef : null,
  MotionAnimations[props.backgroundAnimation]
)

const contentRef = ref<HTMLElement | null>(null)

const contentMotion = useMotion(
  props.animated ? contentRef : null,
  MotionAnimations[props.contentAnimation]
)

const headerRef = ref<HTMLElement | null>(null)
const messagesTopRef = ref<HTMLElement | null>(null)
const messagesBottomRef = ref<HTMLElement | null>(null)
const footerRef = ref<HTMLElement | null>(null)

useSetCssHeightVariable(headerRef, '--sf-modal-header-height')
useSetCssHeightVariable(messagesTopRef, '--sf-modal-messages-top-height')
useSetCssHeightVariable(messagesBottomRef, '--sf-modal-messages-bottom-height')
useSetCssHeightVariable(footerRef, '--sf-modal-footer-height')

const close = () => {
  if (props.animated) {
    closeAfterAnimation(effectiveClose)
  } else {
    effectiveClose()
  }
}

const closeAfterAnimation = (cb: () => void) => {
  contentMotion.leave(() => {})
  backgroundMotion.leave(() => {
    cb()
  })
}

const effectiveClose = () => {
  emit('close')
  window.eventHub.$emit('modal-closed')
}

const handleBackdropClick = () => {
  if (props.closeOnBackdropClick) {
    close()
  } else {
    emit('closeAttempt')
  }
}

const { handleMouseDown, handleMouseUp } = useBackdropClick(handleBackdropClick)

const sizeClassName = useModalClassName(props.size)

const backdropClassNames = computed(() => {
  return {
    'tw-bg-modal-overlay-docked': props.variant === modalVariants.docked,
    'tw-top-subheader': props.variant === modalVariants.docked,
    // Let the backdrop scroll so the whole modal can be scrolled into view
    // without the body clipping open dropdown menus.
    'tw-overflow-y-auto': props.stickyChrome,
    'tw-overflow-hidden': !props.stickyChrome,
  }
})

const outerClassNames = computed(() => {
  return {
    'sf-modal-top': props.variant === modalVariants.default,
    'tw-top-subheader': props.variant === modalVariants.docked,
  }
})

const isFullscreen = computed(() => props.size === modalSizes.fullscreen)
const isFullHeight = computed(
  () => isFullscreen.value || props.size === modalSizes.xxl
)

const containerClassNames = computed(() => {
  return {
    'tw-ml-auto': props.align === modalAlign.right,
    'tw-mr-auto': props.align === modalAlign.left,
    'tw-p-4': props.variant === modalVariants.docked,
    'tw-pt-[2.5vh]': isFullHeight.value,
    'tw-max-w-full': isFullscreen.value,
  }
})

const computedInnerClasses = computed(() => {
  return {
    'tw-h-modal-docked': props.variant === modalVariants.docked,
    'tw-flex': props.variant === modalVariants.docked || isFullHeight.value,
    'tw-flex-col': props.variant === modalVariants.docked || isFullHeight.value,
    'tw-h-[95vh]': isFullHeight.value,
  }
})

const contentClassNames = computed(() => {
  return {
    'sf-modal-content': true,
    '!tw-p-0': props.contentPadding === 0,
    'tw-max-h-modal-content-height': props.scrollableContent,
    'tw-overflow-y-auto': props.scrollableContent,
    'tw-flex-1': props.variant === modalVariants.docked,
    [props.contentClassName]: !!props.contentClassName,
  }
})

const footerClassNames = computed(() => {
  return {
    'sf-modal-footer': true,
    'sf-modal-footer-align-left': props.footerAlign === footerAlignConst.left,
    'sf-modal-footer-align-right': props.footerAlign === footerAlignConst.right,
    '!tw-justify-stretch': props.footerAlign === footerAlignConst.stretch,
    '!tw-p-0': props.footerPadding === 0,
  }
})

const closeButtonClasses = computed(() => {
  return {
    'sf-modal-close': true,
    'sf-invisible': props.hideClose,
  }
})

const handleCloseButtonClick = () => {
  close()
}

const onKeyDown = (event: KeyboardEvent) => {
  // you can prevent closing the modal by calling event.preventDefault() in subcomponent
  if (event.defaultPrevented) {
    return event
  }

  if (event.key === 'Escape' && props.closeOnEscape) {
    close()
  }

  if (event.key === 'Escape' && !props.closeOnEscape) {
    emit('closeAttempt')
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown)
})
</script>

<style scoped lang="scss">
.sf-modal-content {
  &:empty {
    display: none;
  }
}

// Sticky-chrome mode: with the backdrop scrolling (and the content's stacking
// context dropped above), pin the header and footer to the viewport edges so
// the fields scroll between them and it reads as a body-only scroll — without
// turning the content into an overflow container that would clip dropdowns. An
// opaque background and higher z-index keep the scrolling fields behind them;
// the matching radii keep the card corners once the bars are stuck.
.sf-modal-sticky-chrome {
  .sf-modal-header,
  .sf-modal-footer {
    position: sticky;
    z-index: 20;
    background-color: theme('colors.sf-snow.lighter');
  }

  .sf-modal-header {
    top: 0;
    border-radius: 8px 8px 0 0;
  }

  .sf-modal-footer {
    bottom: 0;
    border-top: 1px solid theme('colors.sf-snow.dark');
    border-radius: 0 0 8px 8px;
  }
}
</style>
